/**
 * Human-readable justification for one assignment.
 *
 * The original manual process failed on one question above all others: "why has
 * she got 14:30 when she asked for 13:00?". A schedule nobody can interrogate
 * gets overridden until it is a manual schedule again, so this module exists to
 * make every placement answerable in one sentence.
 *
 * Two rules govern everything below:
 *
 *  1. **Only real numbers.** Every clause is assembled from the run that
 *     actually happened - the entry's own guard rails, the blockers the rejected
 *     candidates really produced, the weighted values the scorer really
 *     computed. There is no template prose and no fallback filler.
 *  2. **Never claim a constraint that did not fire.** The middle clause is built
 *     exclusively from candidates *nearer to the preferred time than the chosen
 *     slot* that came back infeasible. When nothing nearer was rejected the
 *     sentence says so, because "coverage forced this" is a lie the manager will
 *     eventually catch us in.
 *
 * `scoring.js` writes each term's `note` as finished prose containing its
 * numbers, so the factors below are a ranking exercise, not a second attempt at
 * explaining the maths.
 *
 * Pure logic: no DOM, no storage, nothing handed in is mutated.
 */

import { formatDuration, formatMinutes } from '../core/time.js';

/** How many neighbouring slots the panel lists as alternatives. */
const MAX_ALTERNATIVES = 4;
/** Factor list length: at least three, never more than five. */
const MIN_FACTORS = 3;
const MAX_FACTORS = 5;
/** Below this a weighted term is treated as having had no say either way. */
const NEUTRAL_EPSILON = 0.005;

/**
 * Explain one assignment.
 *
 * @param {object} input
 * @param {object} input.entry EligibilityResult the assignment came from.
 * @param {object} input.assignment The Assignment (or draft) being explained.
 * @param {object} input.breakdown The ScoreBreakdown for the chosen slot.
 * @param {Array} input.alternatives Other candidates considered, either as
 *   `{ candidate, breakdown }` pairs from the solver or already-flat records.
 * @param {object} input.context The DayContext, used only for the day config.
 * @returns {{ summary: string, factors: object[], alternatives: object[] }}
 */
export function explainAssignment({ entry, assignment, breakdown, alternatives, context } = {}) {
  const chosen = minutes(assignment?.slotMinutes);
  const others = flattenAlternatives(alternatives).filter((item) => item.slotMinutes !== chosen);

  return {
    summary: buildSummary({ entry, assignment, chosen, others, context }),
    factors: buildFactors(breakdown),
    alternatives: buildAlternatives(others, chosen, breakdown),
  };
}

/* -------------------------------------------------------------------------- */
/* Summary                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The one-sentence answer, in three clauses: where the shift starts, what stood
 * in the way of something nearer, and why this slot won.
 *
 * Each clause degrades honestly rather than disappearing - an assignment with no
 * shift, no preferred time or no score breakdown still gets a true sentence,
 * which is what a manually dragged lunch needs.
 */
function buildSummary({ entry, assignment, chosen, others, context }) {
  const who = displayName(entry, assignment);
  const at = formatMinutes(chosen);

  if (!Number.isFinite(chosen)) {
    return `${who} has no lunch time yet, so there is nothing to explain.`;
  }

  // A human decision gets stated as a human decision, up front. Opening with
  // "was assigned <time> because ..." frames a drag as rule-based reasoning even
  // when the trailing clause admits otherwise, and a manager reading it would
  // reasonably conclude the scheduler had endorsed the time. It did not - it was
  // simply told. The shift is still quoted so the panel remains useful, and any
  // rule the placement breaks is reported separately as a warning.
  if (assignment?.manual === true || assignment?.locked === true) {
    const verb = assignment.locked === true && assignment.manual !== true
      ? 'is locked to'
      : 'was moved to';
    const shiftClause = describeShift(entry);
    const pinned = assignment.locked === true
      ? ' It is locked, so regenerating the week will leave it where it is.'
      : '';
    return `${who} ${verb} ${at} by hand, not by the scheduler`
      + `${shiftClause ? `, on a shift that runs ${shiftClause}` : ''}.${pinned}`;
  }

  const clauses = [];

  const start = minutes(entry?.shift?.startMinutes);
  if (Number.isFinite(start)) {
    const end = minutes(entry?.shift?.endMinutes);
    clauses.push(
      Number.isFinite(end)
        ? `the shift runs ${formatMinutes(start)}-${formatMinutes(end)}`
        : `the shift starts at ${formatMinutes(start)}`,
    );
  }

  const preferred = minutes(entry?.preferredMinutes);
  const nearer = Number.isFinite(preferred)
    ? others.filter((item) => Math.abs(item.slotMinutes - preferred) < Math.abs(chosen - preferred))
      .sort((a, b) => Math.abs(a.slotMinutes - preferred) - Math.abs(b.slotMinutes - preferred))
    : [];
  const rejected = nearer.filter((item) => !item.feasible && item.blockers.length > 0);

  clauses.push(...rejectionClauses({
    nearer,
    rejected,
    assessedAnything: others.length > 0,
    gotTheIdealSlot: chosen === preferred,
  }));
  clauses.push(closingClause({ chosen, preferred, nearer, entry, assignment, context }));

  return `${who} was assigned ${at} because ${joinClauses(clauses)}.`;
}

/** "07:30-15:30", or an empty string when the shift times are unreadable. */
function describeShift(entry) {
  const start = minutes(entry?.shift?.startMinutes);
  const end = minutes(entry?.shift?.endMinutes);
  if (!Number.isFinite(start)) return '';
  return Number.isFinite(end)
    ? `${formatMinutes(start)}-${formatMinutes(end)}`
    : `from ${formatMinutes(start)}`;
}

/**
 * The middle clauses - what actually stopped a nearer slot.
 *
 * Quotes the blockers verbatim from `scoring.js`, which already wrote them as
 * sentences with their numbers in ("…would drop cover to 1, below the minimum of
 * 2"). At most two are quoted; beyond that the sentence stops being a sentence.
 * Each blocker is returned as its own clause so the caller's list punctuation
 * stays correct however many there are.
 */
function rejectionClauses({ nearer, rejected, assessedAnything, gotTheIdealSlot }) {
  if (rejected.length > 0) return rejected.slice(0, 2).map((item) => item.blockers[0]);
  if (!assessedAnything) return ['no other slot was assessed for this lunch'];
  if (gotTheIdealSlot) return ['nothing had to be ruled out to get there'];
  return nearer.length === 0
    ? ['no slot sits closer to the preferred lunch point']
    : ['no closer slot had to be ruled out on a hard rule'];
}

/** The closing clause - why this slot, stated only as strongly as the run supports. */
function closingClause({ chosen, preferred, nearer, entry, assignment, context }) {
  const at = formatMinutes(chosen);

  if (assignment?.locked === true) return `${at} is locked and was carried through untouched`;
  if (assignment?.manual === true) return `${at} was chosen by hand`;

  if (!Number.isFinite(preferred)) {
    const floor = Number(context?.dayConfig?.minStaffOnDuty);
    return Number.isFinite(floor)
      ? `${at} scored highest of the slots that keep at least ${floor} on duty`
      : `${at} scored highest of the slots available`;
  }

  // A lunch placed outside its own group window only got there because the
  // window was relaxed, so the sentence has to own that rather than calling the
  // slot plainly "valid".
  const window = entry?.window;
  if (Number.isFinite(window?.start) && Number.isFinite(window?.end)
    && (chosen < window.start || chosen > window.end)) {
    return `${at} was the nearest slot to the preferred lunch point of ${formatMinutes(preferred)} `
      + `that kept cover, even though it falls outside the `
      + `${formatMinutes(window.start)}-${formatMinutes(window.end)} lunch window`;
  }

  const distance = Math.abs(chosen - preferred);
  if (distance === 0) return `${at} is exactly the preferred lunch point`;

  const anyNearerWasUsable = nearer.some((item) => item.feasible);
  return anyNearerWasUsable
    ? `${at} scored highest overall, ${formatDuration(distance)} from the preferred lunch point of `
      + `${formatMinutes(preferred)}`
    : `${at} is the closest valid slot to the preferred lunch point of ${formatMinutes(preferred)}`;
}

/** "a, b, and c" - Oxford comma, because these clauses are long. */
function joinClauses(clauses) {
  const parts = clauses.filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? '';
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/* -------------------------------------------------------------------------- */
/* Factors                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The three to five terms that actually moved this decision.
 *
 * Ranked by absolute *weighted* value rather than raw value: a term the manager
 * has turned down to a weight of 0.1 did not influence anything, however
 * extreme its raw score, and showing it would be misleading. Terms that scored a
 * flat zero are only padded in to reach the minimum of three, so the panel is
 * never a single lonely row.
 */
function buildFactors(breakdown) {
  const terms = Array.isArray(breakdown?.terms) ? breakdown.terms : [];
  if (terms.length === 0) return [];

  const ranked = [...terms].sort((a, b) => {
    const byWeight = Math.abs(Number(b.weighted) || 0) - Math.abs(Number(a.weighted) || 0);
    if (byWeight !== 0) return byWeight;
    // Stable, deterministic tiebreak: the canonical term order.
    return terms.indexOf(a) - terms.indexOf(b);
  });

  const chosen = ranked.filter(
    (item, index) => index < MIN_FACTORS || Math.abs(Number(item.weighted) || 0) > NEUTRAL_EPSILON,
  );

  return chosen.slice(0, MAX_FACTORS).map((item) => ({
    key: item.key,
    label: item.label,
    detail: item.note,
    impact: impactOf(item.weighted),
  }));
}

function impactOf(weighted) {
  const value = Number(weighted) || 0;
  if (value > NEUTRAL_EPSILON) return 'positive';
  if (value < -NEUTRAL_EPSILON) return 'negative';
  return 'neutral';
}

/* -------------------------------------------------------------------------- */
/* Alternatives                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The nearest few slots that were considered and not used.
 *
 * Nearest rather than best-scoring, because the question behind the panel is
 * always "why not the slot just before this one?" - and a slot that was
 * infeasible has no score to rank it by in the first place.
 */
function buildAlternatives(others, chosen, breakdown) {
  const chosenTotal = Number(breakdown?.total);

  return [...others]
    .sort((a, b) => {
      const byDistance = Math.abs(a.slotMinutes - chosen) - Math.abs(b.slotMinutes - chosen);
      return byDistance !== 0 ? byDistance : a.slotMinutes - b.slotMinutes;
    })
    .slice(0, MAX_ALTERNATIVES)
    .sort((a, b) => a.slotMinutes - b.slotMinutes)
    .map((item) => ({
      slotMinutes: item.slotMinutes,
      // -Infinity does not survive JSON, and an infeasible slot has no score
      // worth comparing anyway.
      score: Number.isFinite(item.total) ? item.total : null,
      feasible: item.feasible,
      blockers: item.blockers,
      note: alternativeNote(item, chosenTotal),
    }));
}

function alternativeNote(item, chosenTotal) {
  if (!item.feasible) {
    return item.blockers.length > 0
      ? `Ruled out: ${item.blockers[0]}.`
      : `${formatMinutes(item.slotMinutes)} was not usable.`;
  }
  if (Number.isFinite(item.total) && Number.isFinite(chosenTotal)) {
    const behind = chosenTotal - item.total;
    return behind > 0
      ? `Usable, but scored ${behind.toFixed(2)} lower.`
      : `Usable, and scored within ${Math.abs(behind).toFixed(2)} of the slot chosen.`;
  }
  return `${formatMinutes(item.slotMinutes)} was usable.`;
}

/* -------------------------------------------------------------------------- */
/* Input shaping                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Accept either shape of alternative.
 *
 * The solver hands over `{ candidate, breakdown }` pairs because that is what it
 * has; a test or a UI replaying a stored explanation has only the flat record.
 * Normalising here keeps both callers honest without either having to know about
 * the other.
 */
function flattenAlternatives(alternatives) {
  if (!Array.isArray(alternatives)) return [];
  return alternatives
    .map((item) => {
      const slotMinutes = Number(item?.candidate?.slotMinutes ?? item?.slotMinutes);
      if (!Number.isFinite(slotMinutes)) return null;
      const breakdown = item?.breakdown ?? item;
      return {
        slotMinutes,
        total: Number(breakdown?.total ?? item?.score),
        feasible: breakdown?.feasible === true,
        blockers: Array.isArray(breakdown?.blockers) ? breakdown.blockers : [],
      };
    })
    .filter(Boolean);
}

/**
 * A clock value, or NaN when there is not really one.
 *
 * `Number(null)` is 0, and a ShiftRecord for a blank or absent cell carries
 * `startMinutes: null` - so a plain coercion lets an employee who is not even
 * rostered be described as working "00:00-00:00", and an entry with no
 * `preferredMinutes` be given an ideal lunch point of midnight. Every clause here
 * would rather say nothing than say something false, so the absent case has to
 * stay absent.
 */
function minutes(value) {
  return Number.isFinite(value) ? value : Number.NaN;
}

/** Prefer the rota name; never leave a sentence starting with "undefined". */
function displayName(entry, assignment) {
  const name = String(entry?.name ?? assignment?.name ?? '').trim();
  if (!name) return 'This employee';
  return name.split(/\s+/)[0];
}
